import * as React from 'react'
import { VendorProfileLink } from '@/components/VendorProfileLink'
import { Plus, Sofa, Store } from 'lucide-react'
import { useAssignEventVendor } from '@/hooks/useEvents'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { Card } from '@/components/ui/card'
import { EventAssignmentRow } from '@/components/EventAssignmentRow'
import { VendorCompanyPicker } from '@/components/VendorCompanyPicker'
import { ASSIGNABLE_SERVICES } from '@/lib/eventVendors'
import type { EventDetailDto, EventVendorDto } from '@lib/dto/event.dto'
import { VENDOR_SERVICE_LABELS, type VendorService } from '@lib/enums/vendor-service.enum'
import { possessive } from '@lib/helpers/format'

type AssignedVendor = Extract<EventVendorDto, { connection: 'assigned' }>
type RentalVendor = Extract<EventVendorDto, { connection: 'rental' }>

/** A company here through its rented pieces: listed, never added or taken off by hand. */
function RentalRow({ vendor }: { vendor: RentalVendor }) {
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <Sofa className="h-4 w-4 shrink-0 text-ink-muted" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          <VendorProfileLink vendorId={vendor.vendorId} name={vendor.companyName} />
          <span className="font-normal text-ink-muted"> · Furniture</span>
        </p>
        <p className="truncate text-xs text-ink-muted">
          On the event while its furniture is rented for it.
        </p>
      </div>
    </li>
  )
}

/** Pick what the company is here for, then find or set it up. */
function AddVendor({ event, forPlanner, onDone }: { event: EventDetailDto; forPlanner: boolean; onDone: () => void }) {
  const assign = useAssignEventVendor(event.id)
  const [service, setService] = React.useState<VendorService>('floral')
  const added = new Set(
    event.vendors.filter((v) => v.connection === 'assigned' && v.service === service).map((v) => v.vendorId),
  )
  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-end justify-between gap-3">
        <Field label="For" htmlFor="event-vendor-service">
          <Select
            id="event-vendor-service"
            value={service}
            onChange={(e) => {
              const next = ASSIGNABLE_SERVICES.find((s) => s === e.target.value)
              if (next) setService(next)
            }}
          >
            {ASSIGNABLE_SERVICES.map((s) => (
              <option key={s} value={s}>
                {VENDOR_SERVICE_LABELS[s]}
              </option>
            ))}
          </Select>
        </Field>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
      <VendorCompanyPicker
        source={
          forPlanner
            ? { kind: 'event', eventId: event.id, venueName: event.venue.name }
            : { kind: 'venue', venueId: event.venueId }
        }
        service={service}
        idPrefix="event-vendor"
        isAdded={(vendorId) => added.has(vendorId)}
        onChoose={async (input) => {
          await assign.mutateAsync(input)
        }}
      />
    </Card>
  )
}

/** The way to add a vendor: a button, then the picker. */
function VendorAdder({
  event,
  forPlanner,
  adding,
  onAdding,
}: {
  event: EventDetailDto
  forPlanner: boolean
  adding: boolean
  onAdding: (adding: boolean) => void
}) {
  return adding ? (
    <AddVendor event={event} forPlanner={forPlanner} onDone={() => onAdding(false)} />
  ) : (
    <Button variant="outline" size="sm" onClick={() => onAdding(true)}>
      <Plus className="h-4 w-4" /> Add a vendor
    </Button>
  )
}

/**
 * Everyone else working the event (docs/planner-vendors.md, decisions 13, 17
 * and 25): the companies added for a service — a florist, a photographer — and
 * those here because their furniture is rented for it. Each sees the event
 * read-only and changes nothing. The planner has its own section.
 *
 * The venue's staff add them from any company on the platform. The event's
 * planner adds them too, from the venue's vendors or a company new to the
 * platform, and the venue's staff are told (docs/vendor-growth.spec.md).
 */
export function EventVendorsPanel({
  event,
  canEdit,
  forPlanner = false,
}: {
  event: EventDetailDto
  /** Whether the viewer can edit events, and so add vendors, invite their contacts, or take them off. */
  canEdit: boolean
  /** The viewer is the event's planner, who picks from the venue's vendors. */
  forPlanner?: boolean
}) {
  const [adding, setAdding] = React.useState(false)
  const assigned = event.vendors.filter((v): v is AssignedVendor => v.connection === 'assigned')
  const renting = event.vendors.filter((v): v is RentalVendor => v.connection === 'rental')

  return (
    <div className="space-y-4">
      {event.vendors.length === 0 ? (
        <p className="rounded-md border border-line px-4 py-3 text-sm text-ink-muted">
          No other vendors on this event.
          {canEdit
            ? ' Add the DJ, the photographer, the florist or anyone else working it, and they can see the event, its timeline and its floor plans, read-only.'
            : null}
        </p>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
          {assigned.map((vendor) => (
            <EventAssignmentRow
              key={vendor.assignmentId}
              event={event}
              assignment={vendor}
              icon={<Store />}
              category={VENDOR_SERVICE_LABELS[vendor.service]}
              removeDescription={
                forPlanner
                  ? `They lose the event at once. Their contact and ${possessive(event.venue.name)} staff are emailed.`
                  : 'They lose the event at once, and their contact is emailed.'
              }
              canEdit={canEdit}
            />
          ))}
          {renting.map((vendor) => (
            <RentalRow key={vendor.vendorId} vendor={vendor} />
          ))}
        </ul>
      )}
      {canEdit ? <VendorAdder event={event} forPlanner={forPlanner} adding={adding} onAdding={setAdding} /> : null}
    </div>
  )
}
